import { useMemo } from 'react'

import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import {
  NetworkName,
  TransactionHistoryItem,
  RailgunWalletBalanceBucket
} from '@railgun-community/shared-models'

import { getPrivateHistoryItemBalanceBucket, type PrivateHistoryItem } from '@web/utils/railgunTx'

const PENDING_BALANCE_BUCKETS = new Set([
  RailgunWalletBalanceBucket.ShieldPending,
  RailgunWalletBalanceBucket.ProofSubmitted,
  RailgunWalletBalanceBucket.MissingInternalPOI,
  RailgunWalletBalanceBucket.MissingExternalPOI
])

const FAILURE_BALANCE_BUCKETS = new Set([RailgunWalletBalanceBucket.ShieldBlocked])

type AmountWithPOI = { hasValidPOIForActiveLists?: boolean }

const hasInvalidPOIForActiveLists = (item: PrivateHistoryItem): boolean => {
  const check = (list: AmountWithPOI[] | undefined) =>
    (list ?? []).some((x) => x.hasValidPOIForActiveLists === false)
  return (
    check(item.changeERC20Amounts) ||
    check(item.receiveERC20Amounts) ||
    check(item.receiveNFTAmounts) ||
    check(item.transferERC20Amounts) ||
    check(item.unshieldERC20Amounts)
  )
}

export const usePrivateTransactionStatus = ({ item }: { item: PrivateHistoryItem }) => {
  const { status, balanceBucket, validPOI } = useMemo(() => {
    const balanceBucket = getPrivateHistoryItemBalanceBucket(item)
    const hasInvalidPOI = hasInvalidPOIForActiveLists(item)

    if (FAILURE_BALANCE_BUCKETS.has(balanceBucket)) {
      return { status: AccountOpStatus.Failure, balanceBucket }
    }

    if (hasInvalidPOI) {
      return { status: AccountOpStatus.BroadcastedButNotConfirmed, balanceBucket }
    }

    if (PENDING_BALANCE_BUCKETS.has(balanceBucket)) {
      return { status: AccountOpStatus.BroadcastedButNotConfirmed, balanceBucket }
    }

    return {
      status: AccountOpStatus.Success,
      balanceBucket,
      validPOI: !hasInvalidPOI
    }
  }, [item])

  return {
    status,
    balanceBucket,
    validPOI
  }
}
